import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router';

import { RequirePermission } from '@/app/guards';
import { headingForPath } from '@/app/shell/nav';
import { NavSlideOver } from '@/app/shell/nav-slide-over';
import { Sidebar } from '@/app/shell/sidebar';
import { Topbar } from '@/app/shell/topbar';
import { ViewAsBanner } from '@/app/shell/view-as-banner';
import { FavoritesBar } from '@/features/favorites/favorites-bar';
import { useAppearanceSync } from '@/features/personalization/use-appearance';
import { useMe } from '@/lib/session';
import { useWideFrame } from '@/lib/use-viewport';

/**
 * The frame every signed-in screen is drawn inside (§4.3): the menu column,
 * the bar across the top, and the page — the only part that scrolls. Content
 * is centered and never wider than 1340px, with 30px above, 32px at each side
 * and 60px below (§4.3.4). Everything is laid out with logical properties, so
 * dir="rtl" mirrors the whole frame.
 *
 * Four universal rules are properties of these six lines rather than of any
 * screen, which is why they are named here and nowhere else:
 *
 *   GEN-22 / NFR-15  "Only the page area may scroll. The side menu, the top
 *     bar and the user card must stay put." `overflow-hidden` on the frame and
 *     `overflow-y-auto` on the <main> is the whole of it. NFR-15's sideways
 *     half is kept one level down, by `<Table>`'s own `overflow-x-auto`
 *     container: a wide table scrolls inside itself rather than pushing the
 *     page body sideways.
 *   GEN-23 / NFR-16  "Page content must be centered and must not grow wider
 *     than 1340 pixels." `mx-auto max-w-[1340px]`, once, so no screen can
 *     disagree with another about how wide the product is.
 *   NFR-02  every offset here is a LOGICAL property (`px-8`, `pt-30`), never
 *     left/right, so Arabic mirrors the frame without a second stylesheet.
 *
 * ── D25: THE FRAME HAS TWO SHAPES, AND THIS IS THE ONLY LINE THAT CHOOSES ──
 *
 * D25 (delegated ruling, 26 Aug 2026) — "narrow screens → slide-over menu
 * behind a button. Desktop unchanged." Above `WIDE_FRAME_QUERY` the menu
 * column is mounted exactly as it always was and no reveal button exists
 * anywhere; below it the column is not mounted, the top bar grows a button at
 * its leading edge, and that button opens `<NavSlideOver>`.
 *
 * The ruling is REVERSIBLE. Reversing it is deleting `wide` from this
 * function: `<Sidebar>` goes back to unconditional, `openMenu` stops being
 * passed, and nothing else in the product knows this decision was made.
 *
 * NFR-19 — the spec left "whether the system must work on a telephone" as a
 * decide-at-build-time question, and delegated ruling D40 (27 Aug 2026)
 * answered it: "narrow-screen support is D25's slide-over frame; full
 * telephone operation is Phase 2, recorded as a reversible deviation per
 * screen where it pinches." The measurable half is enforced HERE — this
 * chooser is what makes every screen reachable and its menu usable on a
 * telephone-width window, pinned down to 320px by d25-narrow-nav.test.tsx —
 * and the citation deliberately sits on the frame, not on any screen,
 * because the frame is the whole of what release 1 promises a telephone.
 *
 * THE DEVIATION HALF, recorded so nobody reads the citation as more than it
 * says: full telephone OPERATION — data-entry screens laid out for a phone,
 * per NFR-19's own screen classes — is Phase 2 under D40. Where a specific
 * screen pinches on a phone, the deviation is recorded AT THAT SCREEN when
 * it is found, reversibly, per D40's text; no per-screen phone layout is
 * promised or built by this frame.
 */
export function AppShell() {
  const { data: me } = useMe();
  const location = useLocation();
  // ADM-08 / BR-ADM-08-03 — the person's own six, from the server, applied to
  // the document the moment they arrive.
  useAppearanceSync();
  // Group breadcrumb, or a personal screen's own (ADM-04 / ADM-08 §3).
  const heading = headingForPath(location.pathname);

  // D25. Asked ONCE per render and handed down, so the button and the panel it
  // opens cannot read two different frames in one paint.
  const wide = useWideFrame();
  const [menuOpen, setMenuOpen] = useState(false);

  /*
   * NAVIGATING CLOSES IT. Keyed on the path rather than wired into every
   * `<NavLink>`, because the menu holds thirteen links plus the brand block,
   * and a per-link handler is a list somebody adds a fourteenth entry to
   * without noticing. Anything that changes the route closes the panel.
   */
  useEffect(() => setMenuOpen(false), [location.pathname]);

  /*
   * And widening the window closes it too — otherwise a person who opens the
   * menu on a narrow window and then maximises is left with a scrim over a
   * page that already has its menu column back.
   */
  useEffect(() => {
    if (wide) setMenuOpen(false);
  }, [wide]);

  // RequireAuth only renders the shell once /api/me resolved with a session.
  if (!me) return null;

  return (
    <div className="flex h-screen overflow-hidden">
      {/* The desktop menu is a RAIL (owner ruling, Oct 2026, superseding
          SP-01's fixed column): 76px of icons that open on hover over the
          page, and take real width only when pinned. The rail draws its own
          spacer, so this line is still the ONE place the frame's shape is
          decided — `wide` mounts it, nothing else here knows its width. */}
      {wide ? <Sidebar me={me} /> : null}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* G-08 — "Viewing as <name>", above everything, while the Owner
            previews another person. Absent otherwise. */}
        <ViewAsBanner />
        <Topbar heading={heading} openMenu={wide ? undefined : () => setMenuOpen(true)} />
        {/* G-01 — the person's pinned screens; absent until they pin one. */}
        <FavoritesBar />
        <main className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[1340px] px-8 pb-[60px] pt-[30px]">
            {/* BR-PERM-51 / 16.7 — one gate for every screen inside the
                shell, so a route added later inherits it rather than needing
                somebody to remember. It replaces the page and nothing else:
                the menu, the company name and the way back out all stay. */}
            <RequirePermission>
              <Outlet />
            </RequirePermission>
          </div>
        </main>
      </div>
      {/* Last in the frame so it paints over the page without a z-index race
          with anything the page itself draws. Mounted only while open, which
          is what makes "focus returns to the button" an unmount effect rather
          than a piece of state somebody has to remember to reset. */}
      {!wide && menuOpen ? <NavSlideOver me={me} onClose={() => setMenuOpen(false)} /> : null}
    </div>
  );
}
